import { enableProdMode, Injector, NgModuleRef, PlatformRef } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from 'app/app.module';
import { environment } from 'environments/environment';
import { cleanupAngularOverlays, resetBodyLayout } from './bootstrap-cleanup.utils';
import { createBootstrapLifecycle } from './bootstrap-lifecycle';

if (environment.production) {
  enableProdMode();
}

let platformRef: PlatformRef | null = null;
let moduleRef: NgModuleRef<AppModule> | null = null;

const lifecycle = createBootstrapLifecycle({
  bootstrap: bootstrapApp,
  teardown: teardownApp,
});

/** Injector do AppModule completo (telas reais com FullLayout), usado pelas pontes React → Angular. Null quando não montado. */
export function getAngularAppInjector(): Injector | null {
  return moduleRef?.injector ?? null;
}

export function mountAngular(container: HTMLElement): Promise<void> {
  return lifecycle.mount(container);
}

async function bootstrapApp(container: HTMLElement): Promise<void> {
  if (moduleRef) return; // já montado: nada a fazer

  // Defesa: remover qualquer <app-root> órfão antes de criar um novo, para
  // garantir um único <app-root> mesmo após um unmount imperfeito anterior.
  document.querySelectorAll('app-root').forEach((el) => el.remove());

  const host = document.createElement('app-root');
  container.appendChild(host);

  platformRef = platformBrowserDynamic();
  moduleRef = await platformRef.bootstrapModule(AppModule);
}

export function unmountAngular(): Promise<void> {
  return lifecycle.unmount();
}

async function teardownApp(): Promise<void> {
  // As referências são zeradas ANTES de destruir, porque `moduleRef` é a guarda que
  // diz ao bootstrap se já há algo montado. Um `destroy()` que lance deixaria a
  // guarda apontando para um módulo morto, e o mount seguinte retornaria em silêncio
  // sem criar o <app-root> — tela vazia sem erro visível. Zeradas antes, a falha do
  // destroy vira um rebootstrap na próxima montagem.
  const module = moduleRef;
  const platform = platformRef;
  moduleRef = null;
  platformRef = null;

  try {
    module?.destroy();
  } finally {
    platform?.destroy();
    document.querySelectorAll('app-root').forEach((el) => el.remove());

    // Limpeza de overlays/toastr/modais anexados ao <body> que vazaram do
    // destroy(). Roda após o destroy: componentes bem comportados fecham seus
    // próprios overlays no ngOnDestroy; isto é a rede de segurança para o resto.
    cleanupAngularOverlays();
    resetBodyLayout();
  }
}
